import type { JobRole, Patient, UpdatePatient } from '@shared/schemas'
import type { PageProps, QueryDefinition } from '@/core'
import type { PatientsFormValues } from './form'
import type { PatientsVariables } from './variables'

export const PATIENT_KEYS = {
  all: ['patients'] as const,
  list: () => [...PATIENT_KEYS.all].join('/'),
  detail: (id: string) => [...PATIENT_KEYS.all, id].join('/'),
  search: (query: string, limit?: number) =>
    [...PATIENT_KEYS.all, 'search', query, limit].join('/'),
  byCompany: (companyId: string) =>
    [...PATIENT_KEYS.all, 'company', companyId].join('/'),
  jobRoles: () => [...PATIENT_KEYS.all, 'job-roles'].join('/'),
}

export const PATIENT_ENDPOINTS = {
  list: ['api', 'registry/patients'] as [string, string],
  delete: (id: string): [string, string] => ['api', `registry/patients/${id}`],
  create: ['api', 'registry/patients'] as [string, string],
  update: (id: string): [string, string] => ['api', `registry/patients/${id}`],
  detail: (id: string): [string, string] => ['api', `registry/patients/${id}`],
  jobRoles: ['api', 'registry/job-roles'] as [string, string],
}

export type PatientsQueryDefinition = [
  QueryDefinition<'patients', 'query', void, Array<Patient>>,
  QueryDefinition<
    'updatePatient',
    'mutation',
    UpdatePatient & { id: string },
    Patient
  >,
  QueryDefinition<'jobRoles', 'query', void, Array<JobRole>>,
]

export const patientsQueries: NonNullable<
  PageProps<
    PatientsFormValues,
    PatientsQueryDefinition,
    PatientsVariables
  >['queries']
> = [
  {
    key: 'patients',
    type: 'query',
    queryConfig: {
      queryKey: [PATIENT_KEYS.list()],
      endpoint: PATIENT_ENDPOINTS.list,
      enabled: true,
    },
  },
  {
    key: 'updatePatient',
    type: 'mutation',
    mutationConfig: {
      endpoint: ['api', 'registry/patients/:id'],
      method: 'PUT',
      queryKeyToInvalidate: [PATIENT_KEYS.list()],
    },
  },
  {
    key: 'jobRoles',
    type: 'query',
    queryConfig: () => ({
      queryKey: [PATIENT_KEYS.jobRoles()],
      endpoint: PATIENT_ENDPOINTS.jobRoles,
      enabled: true,
    }),
  },
]
